import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation, useNavigate, useNavigation } from 'react-router';
import { ensureServiceWorker } from '@saas/module-core/ui';
import { ChatCallRoot } from '@saas/module-chat/ui';
import { TelephonyIncomingCall } from '@saas/module-telephony/ui';
import { Notice, cx, useFontScale, useIsMobile, useOnline } from '@saas/ui';
import { Header } from './Header.js';
import { ReadOnlyBanner } from '@saas/module-core/ui';
import { MobileTabBar } from './MobileTabBar.js';
import { useNavModel } from './nav.js';
import { Sidebar } from './Sidebar.js';
import { NavigationLayoutProvider, useNavigationLayout } from './navigation-layout.js';

/**
 * 応答的なシェル: 1024px 以上 = 上部ナビ / 左サイドバーを選択、767px 以下 = ヘッダー + ボトムタブ。
 * 読み込み中は上端の細い進捗だけ（画面を覆わない）。オフラインはヘッダー直下のバナー（C-13）。
 */
export function Shell() {
  return (
    <ChatCallRoot>
      <NavigationLayoutProvider><ShellLayout /></NavigationLayoutProvider>
    </ChatCallRoot>
  );
}

function ShellLayout() {
  const { layout } = useNavigationLayout();
  const isChat = useLocation().pathname.startsWith('/chat');
  const isConversation = /^\/chat\/c\//.test(useLocation().pathname);
  const model = useNavModel();
  const nav = useNavigation();
  const navigate = useNavigate();
  const online = useOnline();
  const isMobile = useIsMobile();
  useFontScale();

  // 業務コンソールだけを高密度表示にする。チャットは専用 Shell のため、この class は付かない。
  useLayoutEffect(() => {
    document.documentElement.classList.add('console-compact');
    return () => document.documentElement.classList.remove('console-compact');
  }, []);

  // トーストをタブバーの上に出す（@saas/ui の ToastProvider が --toast-bottom を読む）
  useEffect(() => {
    document.documentElement.style.setProperty('--toast-bottom', isMobile ? 'calc(var(--tabbar-height) + env(safe-area-inset-bottom) + 12px)' : '24px');
    return () => {
      document.documentElement.style.removeProperty('--toast-bottom');
    };
  }, [isMobile]);

  // Web Push の受け口（/sw.js）はログイン済みのときだけ登録する。fetch は扱わないので画面の動きは変わらない。
  // 通知をクリックしたとき、SW が制御下にないタブには postMessage で遷移先を渡してくる
  useEffect(() => {
    void ensureServiceWorker();
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | null;
      if (!data || data.type !== 'navigate' || typeof data.url !== 'string') return;
      const u = new URL(data.url, window.location.origin);
      if (u.origin === window.location.origin) void navigate(u.pathname + u.search + u.hash);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return (
    <div className={cx('console-shell flex min-h-dvh flex-col', isConversation && 'h-dvh overflow-hidden')}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-input focus:bg-card focus:px-4 focus:py-2">
        本文へ移動
      </a>
      <Header model={model} />
      {/* 解約手続き中（03 §6.3 ④: 読み取り専用で閲覧とエクスポートができる期間）。該当しないテナントでは何も描かない */}
      <ReadOnlyBanner />
      {nav.state !== 'idle' ? <div className="nav-progress fixed left-0 top-0 z-50 h-0.5 w-full bg-fg" role="progressbar" aria-label="読み込み中" /> : null}
      {!online ? (
        <div className="px-3 pt-3 sm:px-5">
          <Notice tone="warning" live title="オフラインです">
            接続が戻るまで送信や保存はできません。入力中の内容は画面を閉じなければ残ります。
          </Notice>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        {layout === 'sidebar' ? <Sidebar model={model} /> : null}
        <main id="main" tabIndex={-1} className={cx('min-w-0 flex-1 outline-none', isConversation ? 'flex min-h-0 flex-col px-2 pb-[calc(var(--tabbar-height)+env(safe-area-inset-bottom)+12px)] pt-3 md:px-5 md:pb-5' : 'px-5 pb-[calc(var(--tabbar-height)+env(safe-area-inset-bottom)+24px)] pt-6 sm:px-6 md:pb-12 lg:px-8')}>
          <div className={cx('mx-auto w-full', isChat ? 'max-w-[1600px]' : 'console-content', isConversation && 'flex min-h-0 flex-1 flex-col')}>
            <Outlet />
          </div>
        </main>
      </div>
      <MobileTabBar model={model} />
      {/* SFA 段階 5 §9.4: 着信ポップ（電話連携が無効・通話を読めない人には何も出さない） */}
      <TelephonyIncomingCall />
    </div>
  );
}
